import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { OpsanioIcon } from "src/assets/images/opsanio/opsanio_icon";
import { usePrefersReducedMotion } from "./motion";
import { FlowGlowDefs, FlowPath } from "./flow";

/*
 * "Appliance data-flow" diagram - the signature opsanio picture, ported from the
 * product presentation (docs.html) into the themed homepage idiom.
 *
 * Every source (firewalls, network, servers, virtualisation, identity, cloud) is
 * connected via its native protocol and continuously aggregated into ONE appliance
 * in your data center; the same base then serves five products back out. Data
 * visibly FLOWS along the connectors (inward on the left, outward on the right).
 *
 * Rendered on a navy "stage" so it keeps the presentation's dark look while working
 * in both light and dark page themes (navy-band tokens are theme-independent).
 * All motion uses the reduced-motion-gated CSS classes in homepage-theme.css
 * (.ak-flow, .ak-pulse-ring, .ak-core-glow, .ak-scan), so it renders as a clean
 * static diagram when reduced motion is requested.
 */

const VB = { w: 1040, h: 560 };
const CX = 520;
const CY = 280;
const NW = 232; // source node width
const NH = 60; // node height
const SX = 36; // source column x
const FW = 240; // function node width
const FX = VB.w - FW - 36; // function column x
const APP = { x: CX - 104, y: CY - 78, w: 208, h: 156 };

// 24×24 stroke icons drawn with currentColor.
const I: Record<string, JSX.Element> = {
  firewall: <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM9.5 11.5l2 2 3.5-3.5" />,
  network: (
    <path d="M6 7a2 2 0 100-.01M18 7a2 2 0 100-.01M12 19a2 2 0 100-.01M6.8 8.2l4 8M17.2 8.2l-4 8M6.5 6.8h11" />
  ),
  server: <path d="M4 4h16v6H4V4zM4 14h16v6H4v-6zM7 7h.01M7 17h.01" />,
  virt: <path d="M12 2l8.66 5v10L12 22 3.34 17V7L12 2zM12 7.5v9M7.5 9.75l9 4.5M16.5 9.75l-9 4.5" />,
  identity: (
    <path d="M4 5c0-1.7 3.6-3 8-3s8 1.3 8 3M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 5c0 1.7 3.6 3 8 3s8-1.3 8-3M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
  ),
  cloud: <path d="M7 18a4 4 0 010-8 5 5 0 019.6-1.5A3.5 3.5 0 0117 18H7z" />,
  asset: <path d="M8 2h8v4H8zM6 6h12a1 1 0 011 1v13a1 1 0 01-1 1H6a1 1 0 01-1-1V7a1 1 0 011-1zM9 12h6M9 16h6" />,
  vuln: <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM12 9v3M12 15h.01" />,
  siem: <path d="M3 12h4l2 6 4-14 2 8h6" />,
  traffic: <path d="M2 12c2.5-4 5-4 7.5 0s5 4 7.5 0 5-4 5 0M2 17c2.5-4 5-4 7.5 0s5 4 7.5 0" />,
  patch: <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />,
};

const cys = (n: number, pitch: number): number[] =>
  Array.from({ length: n }, (_, i) => CY + (i - (n - 1) / 2) * pitch);

type NodeDef = { key: string; icon: string; titleFb: string; subFb?: string };

const SRC: NodeDef[] = [
  { key: "firewall", icon: "firewall", titleFb: "Firewalls", subFb: "Regeln · Logs" },
  { key: "network", icon: "network", titleFb: "Netzwerk", subFb: "Switche · Router" },
  { key: "server", icon: "server", titleFb: "Server", subFb: "Dienste · Zertifikate" },
  { key: "virt", icon: "virt", titleFb: "Virtualisierung", subFb: "VMware · K8s" },
  { key: "identity", icon: "identity", titleFb: "Identity", subFb: "AD · Verzeichnis" },
  { key: "cloud", icon: "cloud", titleFb: "Cloud & M365", subFb: "SaaS · Entra" },
];

const FN: NodeDef[] = [
  { key: "asset", icon: "asset", titleFb: "Asset-Management" },
  { key: "vuln", icon: "vuln", titleFb: "Schwachstellen-Mgmt" },
  { key: "siem", icon: "siem", titleFb: "SIEM" },
  { key: "traffic", icon: "traffic", titleFb: "Netzwerk-Analyse" },
  { key: "patch", icon: "patch", titleFb: "Patch-Management" },
];

const iconGroupStyle = { color: "var(--ak-accent-2)" } as const;

export const AutonubilApplianceFlow: React.FC = () => {
  const { translate } = useAnketaI18n();
  const tp = "anketa.autonubil.home.flow";
  const reduced = usePrefersReducedMotion();

  const srcY = cys(SRC.length, 84);
  const fnY = cys(FN.length, 94);

  const Node = ({ x, y, w, def, withSub }: { x: number; y: number; w: number; def: NodeDef; withSub: boolean }) => (
    <g>
      <rect
        x={x}
        y={y - NH / 2}
        width={w}
        height={NH}
        rx={13}
        fill="var(--ak-navy-2)"
        stroke="var(--ak-accent-border)"
        strokeWidth={1.4}
      />
      <rect x={x + 14} y={y - 19} width={38} height={38} rx={10} fill="var(--ak-accent-tint)" />
      <g
        transform={`translate(${x + 21}, ${y - 12})`}
        fill="none"
        stroke="var(--ak-accent-2)"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={iconGroupStyle}
      >
        {I[def.icon]}
      </g>
      <text
        x={x + 62}
        y={withSub ? y - 2 : y + 5}
        fill="var(--ak-on-navy)"
        fontFamily="Manrope, Inter, sans-serif"
        fontWeight={700}
        fontSize={15}
      >
        {translate(`${tp}.${def.key}.title`, { defaultMessage: def.titleFb } as any)}
      </text>
      {withSub && def.subFb && (
        <text x={x + 62} y={y + 15} fill="var(--ak-on-navy-muted)" fontFamily="Inter, sans-serif" fontSize={11.5}>
          {translate(`${tp}.${def.key}.sub`, { defaultMessage: def.subFb } as any)}
        </text>
      )}
    </g>
  );

  return (
    <div
      style={{ width: "100%" }}
      role="img"
      aria-label={translate(`${tp}.aria`, {
        defaultMessage:
          "Infrastructure sources - firewalls, network, servers, virtualisation, identity and cloud - are aggregated into one opsanio appliance in your data center, which serves five products: asset management, vulnerability management, SIEM, network analysis and patch management.",
      } as any)}
    >
      <svg
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", overflow: "visible" }}
      >
        <defs>
          <radialGradient id="apfCoreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--ak-accent-2)" stopOpacity="0.45" />
            <stop offset="55%" stopColor="var(--ak-accent)" stopOpacity="0.12" />
            <stop offset="100%" stopColor="var(--ak-accent)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <FlowGlowDefs id="apfGlow" />

        <circle className="ak-core-glow" cx={CX} cy={CY} r={150} fill="url(#apfCoreGlow)" />

        {/* inbound connectors (sources → appliance): packets travel toward the core */}
        {srcY.map((y, i) => (
          <FlowPath
            key={`in-${i}`}
            id={`apf-in-${i}`}
            glowId="apfGlow"
            reduced={reduced}
            dur={3 + (i % 3) * 0.5}
            d={`M${SX + NW},${y} C${(SX + NW + APP.x) / 2},${y} ${(SX + NW + APP.x) / 2},${CY} ${APP.x},${CY}`}
          />
        ))}
        {/* outbound connectors (appliance → products): packets travel outward */}
        {fnY.map((y, i) => (
          <FlowPath
            key={`out-${i}`}
            id={`apf-out-${i}`}
            glowId="apfGlow"
            reduced={reduced}
            dur={3 + (i % 3) * 0.5}
            d={`M${APP.x + APP.w},${CY} C${(APP.x + APP.w + FX) / 2},${CY} ${(APP.x + APP.w + FX) / 2},${y} ${FX},${y}`}
          />
        ))}

        {/* appliance core */}
        {[0, 1.9].map((delay, i) => (
          <circle
            key={i}
            className="ak-pulse-ring"
            cx={CX}
            cy={CY}
            r={APP.w / 2 + 4}
            fill="none"
            stroke="var(--ak-accent-2)"
            strokeWidth={1.5}
            style={{ animationDuration: "3.8s", animationDelay: `${delay}s` }}
          />
        ))}
        <rect
          x={APP.x}
          y={APP.y}
          width={APP.w}
          height={APP.h}
          rx={16}
          fill="var(--ak-navy-3)"
          stroke="var(--ak-accent-border)"
          strokeWidth={1.5}
        />
        {[0, 1, 2].map((r) => (
          <g key={r}>
            <rect
              x={APP.x + 18}
              y={APP.y + 20 + r * 20}
              width={APP.w - 36}
              height={11}
              rx={4}
              fill="var(--ak-on-navy-surface)"
              stroke="var(--ak-on-navy-border)"
              strokeWidth={1}
            />
            <circle cx={APP.x + APP.w - 28} cy={APP.y + 25.5 + r * 20} r={3} fill="var(--ak-accent-2)" />
          </g>
        ))}
        <line
          className="ak-scan"
          x1={APP.x + 16}
          y1={APP.y + 18}
          x2={APP.x + APP.w - 16}
          y2={APP.y + 18}
          stroke="var(--ak-accent-2)"
          strokeWidth={1.6}
        />
        {/* brand: cube icon (true aspect) + wordmark, centered */}
        <OpsanioIcon x={CX - 66} y={APP.y + APP.h - 68} width={30} height={35} />
        <text
          x={CX - 30}
          y={APP.y + APP.h - 40}
          fill="var(--ak-on-navy)"
          fontFamily="Manrope, Inter, sans-serif"
          fontWeight={800}
          fontSize={23}
          letterSpacing="-0.01em"
        >
          opsanio
        </text>
        <text
          x={CX}
          y={APP.y + APP.h - 16}
          textAnchor="middle"
          fill="var(--ak-accent-2)"
          fontFamily="Inter, sans-serif"
          fontWeight={600}
          fontSize={10}
          letterSpacing="0.22em"
        >
          APPLIANCE
        </text>

        {/* nodes */}
        {SRC.map((def, i) => (
          <Node key={def.key} x={SX} y={srcY[i]} w={NW} def={def} withSub />
        ))}
        {FN.map((def, i) => (
          <Node key={def.key} x={FX} y={fnY[i]} w={FW} def={def} withSub={false} />
        ))}
      </svg>
    </div>
  );
};

export default AutonubilApplianceFlow;
